import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import { apiConfig } from 'types/api-config';
import type {
  SalesforceAccountResponse,
  EntitySearchQueryResult,
} from 'types/api-responses';
import type { RoyaltyCheckResult, RoyaltyCheckStatistics } from './types';

export interface RoyaltyChecksSearchParams extends SearchParams {
  includeLineItems?: boolean;
  payorAccountId?: string;
  payeeAccountId?: string;
  createdByName?: string;
  ownerNumber?: string;
  entityId?: number;
  crmAccountId?: string;
  lineUwi?: string;
  interestTypes?: string[];
  productCategories?: string[];
  sources?: Array<'Salesforce' | 'Provisioned'>;
  name?: string;
  checkNumber?: string;
  payorName?: string;
  payeeName?: string;
  checkAmountMin?: number;
  checkAmountMax?: number;
  lineItemCountMin?: number;
  lineItemCountMax?: number;
  propertyCountMin?: number;
  propertyCountMax?: number;
  checkIssueDateStart?: string;
  checkIssueDateEnd?: string;
  createdAtStart?: string;
  createdAtEnd?: string;
  updatedAtStart?: string;
  updatedAtEnd?: string;
}

export const createRoyaltyChecksSearchAtom = () =>
  searchAtomPair<
    RoyaltyCheckResult,
    RoyaltyChecksSearchParams,
    undefined,
    RoyaltyCheckStatistics
  >({ href: '/queries/royalty_checks' });

export const createPayorPayeeAccountsAtom = () =>
  searchAtomPair<
    SalesforceAccountResponse,
    { text?: string; pageSize?: number }
  >({ href: '/queries/crm/accounts' });

export const createEntitiesAtom = () =>
  searchAtomPair<
    EntitySearchQueryResult,
    { text?: string; pageSize?: number; responseMode?: string }
  >({ href: '/queries/entities' });

export const [exportRoyaltyChecksAtom, exportRoyaltyChecksLoadableAtom] =
  mutationAtomWithLoadable<{ exportId: string }, [RoyaltyChecksSearchParams]>(
    async (_get, _set, params: RoyaltyChecksSearchParams) => {
      const response = await request.post<
        { exportId: string; status: string; message?: string },
        { errors?: unknown; error?: string }
      >(apiConfig.ROYALTY_CHECKS_EXPORT_URL, params, new AbortController());

      if (response.type !== 'success') {
        const message =
          response.type === 'error' && response.data?.error
            ? response.data.error
            : 'Failed to start export. Please try again.';
        throw new Error(message);
      }

      return { exportId: response.data.exportId };
    }
  );
